import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FeedbackTag, FeedbackTagCategory } from 'app/ui/components/molecules/workout-feedback/feedback-tags';
import type { WorkoutFeedbackLabels } from 'app/ui/components/molecules/workout-feedback/types';

const CATEGORY_QUESTION_MESSAGE_ID: Record<FeedbackTagCategory, string> = {
  [FeedbackTagCategory.GymEnvironment]: 'workoutFeedback.categoryQuestion.gymEnvironment',
  [FeedbackTagCategory.Programming]: 'workoutFeedback.categoryQuestion.programming',
  [FeedbackTagCategory.Whiteboard]: 'workoutFeedback.categoryQuestion.whiteboard',
};

const TAG_MESSAGE_ID: Record<FeedbackTag, string> = {
  [FeedbackTag.TooMuchEquipment]: 'workoutFeedback.tag.tooMuchEquipment',
  [FeedbackTag.NotEnoughSpace]: 'workoutFeedback.tag.notEnoughSpace',
  [FeedbackTag.TooManyZones]: 'workoutFeedback.tag.tooManyZones',
  [FeedbackTag.TooDifficultToManage]: 'workoutFeedback.tag.tooDifficultToManage',
  [FeedbackTag.NotEnoughTime]: 'workoutFeedback.tag.notEnoughTime',
  [FeedbackTag.NeededMoreRest]: 'workoutFeedback.tag.neededMoreRest',
  [FeedbackTag.FeltUnclearToInstruct]: 'workoutFeedback.tag.feltUnclearToInstruct',
  [FeedbackTag.CuesNeededMoreDetail]: 'workoutFeedback.tag.cuesNeededMoreDetail',
  [FeedbackTag.ScreensDidntTranslateWell]: 'workoutFeedback.tag.screensDidntTranslateWell',
  [FeedbackTag.TimerIssues]: 'workoutFeedback.tag.timerIssues',
  [FeedbackTag.FlowFeltDisjointed]: 'workoutFeedback.tag.flowFeltDisjointed',
  [FeedbackTag.HardToAdaptOnTheFly]: 'workoutFeedback.tag.hardToAdaptOnTheFly',
};

export const useWorkoutFeedbackLabels = (): WorkoutFeedbackLabels => {
  const intl = useIntl();

  return useMemo<WorkoutFeedbackLabels>(
    () => ({
      title: intl.formatMessage({ id: 'workoutFeedback.title' }),
      overallRatingLabel: intl.formatMessage({ id: 'workoutFeedback.overallRating' }),
      easeOfFollowingLabel: intl.formatMessage({ id: 'workoutFeedback.easeOfFollowing' }),
      runAgainLabel: intl.formatMessage({ id: 'workoutFeedback.runAgain' }),
      ratingValueLabel: (value: number) => intl.formatMessage({ id: `workoutFeedback.rating.${value}` }),
      easeRatingValueLabel: (value: number) => intl.formatMessage({ id: `workoutFeedback.easeRating.${value}` }),
      yes: intl.formatMessage({ id: 'workoutFeedback.yes' }),
      no: intl.formatMessage({ id: 'workoutFeedback.no' }),
      next: intl.formatMessage({ id: 'workoutFeedback.next' }),
      back: intl.formatMessage({ id: 'workoutFeedback.back' }),
      submit: intl.formatMessage({ id: 'workoutFeedback.submit' }),
      selectAllThatApply: intl.formatMessage({ id: 'workoutFeedback.selectAllThatApply' }),
      stepIndicatorLabel: (current: number, total: number) =>
        intl.formatMessage({ id: 'workoutFeedback.stepIndicator' }, { current, total }),
      categoryQuestionLabel: (category) => intl.formatMessage({ id: CATEGORY_QUESTION_MESSAGE_ID[category] }),
      tagLabel: (tag) => intl.formatMessage({ id: TAG_MESSAGE_ID[tag] }),
      shareFeedbackLabel: intl.formatMessage({ id: 'workoutFeedback.shareFeedback' }),
      freeTextPlaceholder: intl.formatMessage({ id: 'workoutFeedback.freeTextPlaceholder' }),
      charCountLabel: (current: number, max: number) =>
        intl.formatMessage({ id: 'workoutFeedback.charCount' }, { current, max }),
      successMessage: intl.formatMessage({ id: 'workoutFeedback.success' }),
      closeLabel: intl.formatMessage({ id: 'workoutFeedback.close' }),
    }),
    [intl],
  );
};
